import { Box, CircularProgress, Avatar, Typography, Tooltip, Chip, Grid } from "@material-ui/core";
import { IconButton, Popover, List, ListItem, ListItemText, ListItemIcon, Button } from '@mui/material';
import { useReducer, useEffect, useContext, useState, useMemo } from "react";
import socket from "../../../hooks/useSocket";
import { AuthContext } from "../../../context/Auth/AuthContext";
import toastError from "../../../errors/toastError";
import api from "../../../services/api";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../../translate/i18n";
import ContactModal from "../../ContactModal";
import ScheduleMessageListModal from "../../ScheduleMessageListModal";
import RenderChatRelatory from '../../RenderChatRelatory';
import ContactTransferModal from '../../ContactTransferModal';
import SingleSelect from "../../InputStyled/singleSelect";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { MoreVert as MoreVertIcon, LoopOutlined as LoopOutlinedIcon, CreditCard as CreditCardIcon, HourglassEmptyOutlined as HourglassEmptyOutlinedIcon, PermContactCalendarOutlined as PermContactCalendarOutlinedIcon, AccessAlarm as AccessAlarmIcon, Chat as ChatIcon, SyncAlt as SyncAltIcon } from '@mui/icons-material';
import MenuIcon from '@material-ui/icons/Menu';
import MessageIcon from '@material-ui/icons/Message';
import SyncAltOutlinedIcon from '@material-ui/icons/SyncAltOutlined';
import { toast } from "react-toastify";

const ITEM_SIZE = 135;
const MAX_VISIBLE_ITEMS = 5;

const sortByOrder = (list) => {
    if (!Array.isArray(list) || list.length == 0) return [];

    return list.slice().sort((a, b) => {
        const orderA = a.status?.order ?? 0;
        const orderB = b.status?.order ?? 0;
        if (orderA !== orderB) return orderA - orderB;
        return String(a.name || "").localeCompare(String(b.name || ""));
    });
};

const sortContactsForColumn = (list) => sortByOrder(list);

const sortSemStatusByRecentTicket = (list) => {
    if (!Array.isArray(list) || list.length == 0) return [];

    return list.slice().sort((a, b) => {
        const dateA = a.Ticket?.createdAt ? new Date(a.Ticket.createdAt).getTime() : 0;
        const dateB = b.Ticket?.createdAt ? new Date(b.Ticket.createdAt).getTime() : 0;
        return dateB - dateA;
    });
};


const statusSortByOrder = (list) => {
    if (!Array.isArray(list) || list.length == 0) return [];
    
    const resp = list.slice().sort((a, b) => {
        const orderA = a?.order;
        const orderB = b?.order;
        return orderA - orderB;
    });

    return resp;
};

const reducer = (state, action) => {

    switch (action.type) {
        case "LOAD_STATUS": {
            // Obtém o status a partir do payload da ação
            const status = action.payload;

            // Atualiza o estado existente com os valores do status, mantendo os não encontrados
            const updatedState = Array.isArray(state)
                ? state.map((s) => status.find((stat) => stat.id == s.id) || s)
                : [];

            // Identifica os novos status que não estão presentes no estado atual
            const newStatus = status.filter(
                (stat) => !state.some((s) => s.id == stat.id)
            );

            // Combina os estados atualizados e os novos, ordenando-os antes de retornar
            return statusSortByOrder([...updatedState, ...newStatus]);
        }

        case "UPDATE_STATUS": {
            const stat = action.payload;
            const updatedState = Array.isArray(state) && state.some((s) => s.id == stat.id)
                ? state.map((s) => (s.id == stat.id ? stat : s))
                : [stat, ...state];

            return statusSortByOrder(updatedState);
        }

        case "DELETE_STATUS": {
            const statusId = action.payload;
            const updatedState = state.filter((s) => s.id != statusId);

            return statusSortByOrder(updatedState);
        }

        case "RESET":
            return [];

        default:
            return state;
    }
};

const reducerContacts = (state, action) => {

    if (action.type == "LOAD_CONTACTS") {
        const contacts = action.payload;
        const newContacts = [];
        contacts.forEach((contact) => {
            const contactIndex = state.findIndex((c) => c.id == contact.id);
            if (contactIndex != -1) {
                state[contactIndex] = contact;
            } else {
                newContacts.push(contact);
            }
        });

        return sortByOrder([...state, ...newContacts]);
    }

    if (action.type == "UPDATE_CONTACTS") {
        const contact = action.payload;
        const contactIndex = state.findIndex((c) => c.id == contact.id);
        if (contactIndex != -1) {
            state[contactIndex] = Object.assign(state[contactIndex], contact);
            return sortByOrder([...state]);
        } else {
            const response = [...state, contact];
            return sortByOrder(response);
        }
    }

    if (action.type == "DELETE_CONTACTS") {
        const contactId = action.payload;

        const contactIndex = state.findIndex((c) => c.id == contactId);
        if (contactIndex != -1) {
            state.splice(contactIndex, 1);
        }
        return sortByOrder([...state]);
    }

    if (action.type == "REORDER_COLUMN") {
        const { statusId, orderedContacts } = action.payload;
        const withoutColumn = state.filter((c) => String(c.status?.statusId) !== String(statusId));
        return [...withoutColumn, ...orderedContacts];
    }

    if (action.type == "RESET") {
        return [];
    }
};

const useStyles = makeStyles((theme) => ({
    column: {
        width: '350px',
        margin: '0',
        height: '100%',
        maxHeight: '100%',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: '#ffffff',
        borderRadius: '10px',
        boxShadow: '0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 1px 5px 0 rgba(0, 0, 0, 0.2)',
    },
    columnCards: {
        flex: 1,
        minHeight: 0,
        padding: '12px 10px 10px',
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'relative',
        ...theme.scrollbarStyles,
    },
    columnHeader: {
        background: '#0b708c',
        color: '#ffffff',
        display: 'flex',
        flexShrink: 0,
        width: '100%',
        height: '40px',
        borderRadius: '10px 10px 0 0',
        padding: '10px',
        textTransform: 'capitalize',
        fontSize: '1.1rem',
        fontWeight: '700',
        '&>label': {
            height: '100%',
            width: '200px',
            display: 'inline-block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        '&>span': {
            textAlign: 'right',
            display: 'inline-block',
            width: '100px',
            height: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        '&>button': {
            top: '2px',
            width: '15px',
            height: '15px',
            color: '#ffffff',
            left: '5px'
        }
    },
    columnItem: {
        margin: '0',
        backgroundColor: 'white',
        borderRadius: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: '1px solid #e0e0e0',
        height: '120px',
        position: 'relative',
        flexShrink: 0,
    },
    draggableWrapper: {
        marginBottom: 15,
        flexShrink: 0,
        boxSizing: 'border-box',
    },
    header: {
        top: '0',
        position: 'absolute',
        width: '100%',
        paddingLeft: '15px',
        padding: '5px',
        height: '50px',
        '&>h6': {
            display: 'inline-block',
            width: '200px',
            paddingLeft: '5px',
            position: 'absolute',
            height: '25px',
            lineHeight: '25px',
            fontSize: '1.2rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        '&>label': {
            display: 'inline-block',
            width: '200px',
            paddingLeft: '5px',
            position: 'absolute',
            height: '15px',
            lineHeight: '15px',
            top: '30px',
            fontSize: '.9rem',
            fontWeight: '900',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: '#424242'
        },
    },
    profilePicture: {
        height: '40px',
        width: '40px',
        display: 'inline-block',
        '&>svg': {
            height: '100%',
            width: '100%'
        }
    },
    productsAndServices: {
        position: 'absolute',
        width: '100%',
        height: '20px',
        bottom: '50px',
        padding: '5px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        paddingLeft: '15px',
        color: '#616161',
    },
    currencyValues: {
        position: 'absolute',
        width: '100%',
        height: '25px',
        bottom: '25px',
        padding: '5px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: '#757575',
        fontSize: '1rem',
        paddingLeft: '15px',
        '&>span': {
            marginRight: '5px'
        }
    },
    footer: {
        bottom: 0,
        width: '100%',
        height: '25px',
        lineHeight: '25px',
        position: 'absolute',
        paddingLeft: '15px',
        '&>div': {
            display: 'inline-block',
            width: '30px',
            height: '15px',
        },
        '&>label': {
            color: '#616161',
            float: 'right',
            marginTop: '5px',
            marginRight: '10px',
            display: 'inline-block',
            fontSize: '0.85rem',
            fontWeight: '900',
        },
    },
    icon: {
        width: '30px',
        height: '30px',
        margin: '0 2px',
        borderRadius: '100%',
        background: '#e0e0e0',
        color: '#0B708C',
        "&:hover": {
            background: '#bdbdbd'
        },
        "&>svg": {
            width: '20px',
            height: '20px'
        },
    },
    shortName: {
        width: '30px',
        height: '30px',
        background: '#0B708C',
        borderRadius: '100%',
        float: 'right',
        textAlign: 'center',
        paddingTop: '6px',
        color: '#ffffff',
        cursor: 'pointer'
    },
    queue: {
        position: 'absolute',
        left: '0',
        height: '100%',
        minHeight: '50px',
        width: '10px',
        background: '#bdbdbd',
        cursor: 'pointer!important',
        zIndex: '2',
    },
    customPaper: {
        padding: '0px!important',
        boxShadow: '0 2px 2px 0 rgba(0,0,0,0.14), 0 3px 1px -2px rgba(0,0,0,0.12), 0 1px 5px 0 rgba(0,0,0,0.2)',
        width: '250px!important',
        borderRadius: '0!important'
    },
}));

export default function Kanban({ refresh, ...props }) {

    const [status, dispatch] = useReducer(reducer, []);
    const [contacts, dispatchContacts] = useReducer(reducerContacts, []);
    const [loading, setLoading] = useState(true);
    const [columnMeta, setColumnMeta] = useState({});
    const [contactModal, setContactModal] = useState(null);
    const [scheduleModal, setScheduleModal] = useState({ contactId: null, ticketId: null, whatsappId: null });
    const [messageHistoryModal, setMessageHistoryModal] = useState(null);
    const [transferTicketModal, setTransferTicketModal] = useState({ contact: null, ticket: null });
    const { user } = useContext(AuthContext);
    const adminId = user.adminId;
    const classes = useStyles();
    const [anchorEls, setAnchorEls] = useState({});
    const currencyFilter = [
        { key: 'recurringValue', value: 'Valores Recorrentes', disabled: false },
        { key: 'singleValue', value: 'Valores Únicos', disabled: false },
        { key: 'contractValue', value: 'Valores dos Contratos', disabled: false },
    ];
    const [selectedCurrencyFilter, setSelectedCurrencyFilter] = useState([]);
    const [tags, setTags] = useState([]);
    const [anchorPosition, setAnchorPosition] = useState({ top: 0, left: 0 });
    const [actionProps, setActionProps] = useState(null);

    useEffect(() => {

        const customerRelationshipStatusEventName = `customerRelationshipStatus-${adminId}`;
        const customerRelationshipStatusEventFunction = async (data) => {

            if (data.action == "update" || data.action == "create") {
                // caso o status não estiver vinculado ao usuário, não exibir na tela
                if (!Array.isArray(data.status.userIds) || data.status.userIds.includes(user.id) == false) {
                    dispatch({
                        type: "DELETE_STATUS",
                        payload: +data.status.id,
                    });
                } else {
                    dispatch({ type: "UPDATE_STATUS", payload: data.status });
                }
            }

            if (data.action == "delete") {
                dispatch({
                    type: "DELETE_STATUS",
                    payload: +data.statusId,
                });
            }
        };

        const contactEventName = `contact-${adminId}`;
        const contactEventFunction = async (data) => {
            if (data.action == "update" || data.action == "create") {
                dispatchContacts({ type: "UPDATE_CONTACTS", payload: data.contact });
            }
            if (data.action == "delete") {
                dispatchContacts({ type: "DELETE_CONTACTS", payload: +data.contactId });
            }
        };

        const ticketEventName = `ticket-${adminId}`;
        const ticketEventFunction = async (data) => {
            if (data.action == "update") {
                // verificar se o ticket está aberto ou aguardando e possuí contato
                const { ticket } = data;

                if (ticket?.Contact && (ticket.status == 'open' || ticket.status == 'pending')) {
                    // se não existir resgatar informação completa do contato para atualizar
                    (async () => {
                        try {
                            const { data } = await api.get(`/contacts/${ticket.Contact.id}`);
                            dispatchContacts({ type: "UPDATE_CONTACTS", payload: { ...data, Ticket: ticket } });
                        } catch (err) {
                            console.error({ err });
                            toastError(err);
                        }
                    })();
                }
            } else if (data.action == 'delete') {
                const { contactId } = data;
                if (contactId) {
                    // deletar contato somente se for da coluna sem status
                    dispatchContacts({ type: "DELETE_CONTACTS", payload: contactId });
                }
            }
        };

        socket.on(customerRelationshipStatusEventName, customerRelationshipStatusEventFunction);
        socket.on(contactEventName, contactEventFunction);
        socket.on(ticketEventName, ticketEventFunction);

        return () => {
            socket.off(customerRelationshipStatusEventName, customerRelationshipStatusEventFunction);
            socket.off(contactEventName, contactEventFunction);
            socket.off(ticketEventName, ticketEventFunction);
        };
    }, []);

    useEffect(() => {
        dispatch({ type: "RESET" });
        dispatchContacts({ type: "RESET" });
        setColumnMeta({});
        setLoading(true);
    }, [refresh]);

    const fetchColumnContacts = async (statusId, pageNumber, signal) => {
        if (statusId == null || statusId === "") {
            return { contacts: [], hasMore: false, totalContacts: 0 };
        }

        const { data } = await api.get("/contacts/crmList", {
            params: { statusId: Number(statusId), pageNumber },
            signal,
        });
        return data;
    };

    const updateColumnMeta = (statusId, patch) => {
        setColumnMeta((prev) => ({
            ...prev,
            [statusId]: { ...prev[statusId], ...patch },
        }));
    };

    useEffect(() => {
        let isMounted = true;
        const abortController = new AbortController();

        const delayDebounceFn = setTimeout(() => {
            (async () => {
                try {
                    const { data } = await api.get("/customerRelationship/status/list", {
                        params: { filterByUser: 1 },
                        signal: abortController.signal,
                    });
                    if (!isMounted) return;

                    const statusList = Array.isArray(data.status) ? data.status : [];
                    const validStatus = statusList.filter((s) => s?.id != null);

                    if (validStatus.length > 0) {
                        setSelectedCurrencyFilter((prevFilters) => {
                            const newFilters = [...prevFilters];

                            validStatus.forEach((s) => {
                                const exists = newFilters.some((cf) => cf.statusId == s.id);
                                if (!exists) {
                                    newFilters.push({ statusId: s.id, selected: "recurringValue" });
                                }
                            });

                            return newFilters;
                        });

                        const results = await Promise.all(
                            validStatus.map(async (s) => {
                                try {
                                    const columnData = await fetchColumnContacts(s.id, 1, abortController.signal);
                                    return { statusId: s.id, data: columnData };
                                } catch (err) {
                                    if (err.name == "AbortError") return null;
                                    console.error({ err });
                                    toastError(err);
                                    return { statusId: s.id, data: null };
                                }
                            })
                        );

                        if (!isMounted) return;

                        const allContacts = [];
                        const nextMeta = {};

                        validStatus.forEach((s) => {
                            nextMeta[s.id] = {
                                pageNumber: 1,
                                hasMore: false,
                                totalContacts: 0,
                                loading: false,
                            };
                        });

                        results.forEach((result) => {
                            if (!result) return;

                            if (result.data) {
                                allContacts.push(...result.data.contacts);
                                nextMeta[result.statusId] = {
                                    pageNumber: 1,
                                    hasMore: result.data.hasMore ?? false,
                                    totalContacts: result.data.totalContacts ?? 0,
                                    loading: false,
                                };
                            }
                        });

                        dispatch({ type: "LOAD_STATUS", payload: statusList });
                        dispatchContacts({ type: "LOAD_CONTACTS", payload: allContacts });
                        setColumnMeta(nextMeta);
                    } else {
                        dispatch({ type: "LOAD_STATUS", payload: statusList });
                        dispatchContacts({ type: "RESET" });
                        setColumnMeta({});
                    }

                    const { data: dtags } = await api.get("/tags", { signal: abortController.signal });
                    if (!isMounted) return;
                    if (!dtags || !dtags.tags) dtags.tags = [];
                    dtags.tags.unshift({
                        id: 0,
                        adminId: user.adminId,
                        color: "#000000",
                        name: "sem etiqueta",
                        updatedBy: user.id,
                        status: 1,
                    });
                    setTags(dtags.tags);
                } catch (err) {
                    if (!isMounted || err.name == "AbortError") return;
                    console.error({ err });
                    toastError(err);
                } finally {
                    if (isMounted) setLoading(false);
                }
            })();
        }, 500);

        return () => {
            isMounted = false;
            abortController.abort();
            clearTimeout(delayDebounceFn);
        };
    }, [refresh]);

    const reorderColumnContacts = (columnContacts, sourceIndex, destinationIndex) => {
        const reordered = columnContacts.slice();
        const [removed] = reordered.splice(sourceIndex, 1);
        reordered.splice(destinationIndex, 0, removed);
        return reordered.map((contact, index) => ({
            ...contact,
            status: {
                ...contact.status,
                statusId: contact.status?.statusId,
                contactId: contact.id,
                order: index,
            },
        }));
    };

    // Função chamada quando um item é movido
    const onDragEnd = (result) => {

        if (!result.destination) return;

        const contactId = result.draggableId;
        const sourceStatusId = result.source.droppableId;
        const destStatusId = result.destination.droppableId;

        if (destStatusId != sourceStatusId) {
            handleStatusId(destStatusId, contactId);

            const ctcTmp = contacts.find((c) => String(c.id) === String(contactId));
            if (ctcTmp) {
                dispatchContacts({
                    type: "UPDATE_CONTACTS",
                    payload: {
                        ...ctcTmp,
                        status: { statusId: destStatusId, contactId, order: result.destination.index },
                    },
                });
            }

            return;
        }

        if (result.source.index === result.destination.index) return;

        const columnContacts = sortContactsForColumn(filteredContactsByStatus[sourceStatusId] || []);
        const movedContact = columnContacts[result.source.index];

        if (!movedContact?.status?.statusId) {
            toast.error("Este contato não pode ser reordenado nesta coluna.");
            return;
        }

        const orderedContacts = reorderColumnContacts(
            columnContacts,
            result.source.index,
            result.destination.index
        );

        dispatchContacts({
            type: "REORDER_COLUMN",
            payload: { statusId: sourceStatusId, orderedContacts },
        });

        handleCardOrder(sourceStatusId, contactId, result.destination.index);
    };

    // modal de informações de contato
    const handleContactInfoOpen = (contactId) => {
        setContactModal(contactId);
    }

    const handleContactInfoClose = () => {
        setContactModal(null)
    }

    // Modal de agendamento de mensagens 
    const handleScheduleMessageOpen = (contactId, ticketId, whatsappId) => {
        setScheduleModal({ contactId, ticketId, whatsappId });
    };

    const handleScheduleMessageClosed = () => {
        setScheduleModal({ contactId: null, ticketId: null, whatsappId: null });
    };

    // Modal de histórico de atendimento
    const handleMessageHistoryOpen = (ticketId) => {
        setMessageHistoryModal(ticketId);
    }

    const handleMessageHistoryClose = () => {
        setMessageHistoryModal(null);
    }

    // Modal de transferencia de atendimento
    const handleTransferOpen = (contact, ticket) => {
        setTransferTicketModal({ contact, ticket });
    }

    const handleTransferClose = () => {
        setTransferTicketModal({ contact: null, ticket: null });
    }

    // menu de exibição de valores
    const handleClick = (event, id) => {
        setAnchorEls(prev => ({ ...prev, [id]: event.currentTarget }));
    };
    const handleClose = (id) => {
        setAnchorEls(prev => ({ ...prev, [id]: null }));
    };

    // menu de exibição de botões
    const handleButtonsClick = (event, props) => {

        const element = event.currentTarget;
        const bounding = element.getBoundingClientRect();
        setAnchorPosition({ top: bounding.top + 30, left: bounding.left + 30 });
        setActionProps(props);

    };
    const handleButtonsClose = () => {
        setAnchorPosition({ top: 0, left: 0 });
        setActionProps(null);
    };

    const formatNumber = (input) => {
        // Converte o input para string, se não for
        let str = input.toString();
    
        // Separa a parte inteira e decimal
        let [integerPart, decimalPart] = str.split(',');
    
        // Adiciona o separador de milhar na parte inteira
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
        // Junta a parte formatada com a decimal
        return decimalPart ? `${integerPart},${decimalPart}` : integerPart;
    }

    const sumDecimalValues = (values) => {
        const sum = values.reduce((total, value) => {
            // Substitui ',' por '.' e converte para número
            let numericValue = value.replaceAll('.','');
            numericValue = parseFloat(numericValue.replace(',', '.'));
            return total + numericValue;
        }, 0);

        // Formata o resultado de volta para o formato decimal com vírgula
        return formatNumber(sum.toFixed(2).replace('.', ','));
    }

    // Função para calcular os totais de valores.
    const getTotals = (contacts, keys) => {
        return keys.reduce((acc, key) => {
            acc[key] = sumDecimalValues(
                contacts.map(contact => contact.extraInfo.find(ei => ei.name == key)?.value || '0,00')
            );
            return acc;
        }, {});
    };

    const handleSelectedCurrencyFilter = (currencyFilter, statusId) => {

        setSelectedCurrencyFilter(prevFilters => {
            const newFilters = [...prevFilters];
    
            const idx = newFilters.findIndex(cf => cf.statusId == statusId);
            if (idx != -1) newFilters[idx].selected = currencyFilter;
    
            return newFilters;
        });
    }

    const handleStatusId = async (statusId, contactId) => {
		try {
            await api.post("/contacts/customerRelationship/status/update", { statusId, contactId });
		} catch (err) {
			console.error({ err });
			toastError(err);
		}
	};

    const handleCardOrder = async (statusId, contactId, order) => {
		try {
            await api.post("/contacts/customerRelationship/statusOrder/update", { statusId, contactId, order });
		} catch (err) {
			console.error({ err });
			toastError(err);
		}
	};

    // Subcomponente: ContactCard
    const ContactCard = ({ contact }) => {
        const companyIndex = Array.isArray(contact?.extraInfo) ? contact.extraInfo.findIndex(ei => ei.name == 'empresa') : -1;
        return(
        <Box className={classes.columnItem} id={`contact-card-${contact.id}`}>
            <Tooltip title={contact?.Ticket?.Queue?.name || 'Sem Fila'}>
                <Box
                    className={classes.queue}
                    component="div"
                    style={{ background: contact?.Ticket?.Queue?.color }}
                />
            </Tooltip>
            <Box className={classes.header} component="div">
                <Avatar src={contact.profilePicUrl} className={classes.profilePicture} />
                <Typography variant="h6">{contact.name}</Typography>
                <Typography component="label">Empresa: {companyIndex >= 0 ? contact.extraInfo[companyIndex].value != '' ? contact.extraInfo[companyIndex].value  : 'nenhum registro': 'nenhum registro'}</Typography>
            </Box>
            {renderActions(contact)}
            <Box className={classes.productsAndServices}>
                Produto(s) | Serviço(s): {renderProducts(contact.productsAndServices)}
            </Box>
            <Box className={classes.currencyValues}>
                {renderValues(contact)}
            </Box>
            <Box className={classes.footer}>
                {renderTags(contact.tagsId, contact?.id)}
                {contact?.Ticket?.id && (
                    <Typography component="label" key={`ticket-${contact.Ticket.id}`}>
                        #{contact.Ticket.id}
                    </Typography>
                )}
            </Box>
        </Box>
    )};

    // Função para renderizar tags
    const renderTags = (tags, contactId) => (
        Array.isArray(tags) ? tags.map(tag => (
            <Tooltip 
                arrow 
                title={tag.name || "Não identificado"} 
                key={`tags-contact-${contactId}-${tag.id}`}
            >
                <Chip size="small" style={{ backgroundColor: tag.color, margin: '0 3px' }} />
            </Tooltip>
        )) : null
    );

    // Função para renderizar valores
    const renderValues = contact => {
        const icons = [
            { icon: <LoopOutlinedIcon />, label: 'recurringValue', tooltip: 'Valores Recorrentes' },
            { icon: <CreditCardIcon />, label: 'singleValue', tooltip: 'Valores Únicos' },
            { icon: <HourglassEmptyOutlinedIcon />, label: 'contractTime', tooltip: 'Prazo Contratual' }
        ];

        return icons.map(({ icon, label, tooltip }, index) => (
            <Tooltip arrow title={tooltip} key={`valuesForContact-${contact?.id}-${index}`}>
                <Box component="span">
                    {icon} {label == 'contractTime' ? `${contact.extraInfo.find(e => e.name == label)?.value || '0'} Mes(es)` : `R$ ${formatNumber(contact.extraInfo.find(e => e.name == label)?.value || '0,00')}`}
                </Box>
            </Tooltip>
        ));
    };

    // Função para renderizar produtos e serviços
    const renderProducts = products =>
        Array.isArray(products) && products.length > 0
            ? products.map(ps => ps['CustomerRelationshipProductAndService.name']).join(", ")
            : 'nenhum registro.';

    // Função para renderizar ações   
    const renderActions = (contact) => {
        if (!contact) return null;

        const { id, Ticket } = contact;
        const whatsappId = Ticket?.whatsappId;
        const isTicketClosed = Ticket?.status == 'closed';

        const props = {
            contact,
            Ticket,
            whatsappId,
            isTicketClosed
        }

        return (
            <IconButton
                aria-label={`more-icon-button${id}`}
                onClick={(event) => handleButtonsClick(event, props)}
                id={`more-icon-button${id}`}
                key={`more-icon-button${id}`}
                style={{
                    position: 'absolute',
                    top: '5px',
                    right: '5px'
                }}
            >
                <MenuIcon />
            </IconButton>
        );
    };

    const filteredContactsByStatus = useMemo(() => {
        return status.reduce((acc, s) => {
            const isSemStatusColumn = String(s.name).toLowerCase() === "sem status";
            const columnContacts = contacts.filter((contact) =>
                isSemStatusColumn
                    ? !contact.status || contact?.status?.statusId == s.id
                    : contact?.status?.statusId == s.id
            );
            acc[s.id] = isSemStatusColumn
                ? sortSemStatusByRecentTicket(columnContacts)
                : sortContactsForColumn(columnContacts);
            return acc;
        }, {});
    }, [contacts, status]);

    const totalsByStatus = useMemo(() => {
        return status.reduce((acc, s) => {
            acc[s.id] = getTotals(filteredContactsByStatus[s.id], ['recurringValue', 'singleValue', 'contractValue']);
            return acc;
        }, {});
    }, [filteredContactsByStatus]);

    const loadMoreContacts = async (statusId) => {
        const meta = columnMeta[statusId];
        if (!meta?.hasMore || meta?.loading || loading) return;

        const nextPage = (meta.pageNumber || 1) + 1;
        updateColumnMeta(statusId, { loading: true });

        try {
            const data = await fetchColumnContacts(statusId, nextPage);
            dispatchContacts({ type: "LOAD_CONTACTS", payload: data.contacts });
            updateColumnMeta(statusId, {
                pageNumber: nextPage,
                hasMore: data.hasMore ?? false,
                totalContacts: data.totalContacts ?? meta.totalContacts,
                loading: false,
            });
        } catch (err) {
            console.error({ err });
            toastError(err);
            updateColumnMeta(statusId, { loading: false });
        }
    };

    const handleColumnScroll = (statusId) => (e) => {
        const meta = columnMeta[statusId];
        if (!meta?.hasMore || meta?.loading || loading) return;

        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMoreContacts(statusId);
        }
    };

    const getColumnCountLabel = (statusId) => {
        const loaded = filteredContactsByStatus[statusId]?.length ?? 0;
        const total = columnMeta[statusId]?.totalContacts;
        if (total != null && total > loaded) {
            return `${loaded} / ${total}`;
        }
        return String(loaded);
    };

    return (
        <Box {...props}>
            {
                loading == true
                    ?
                    <CircularProgress color="primary" style={{ margin: "0 auto", display: "block" }} />
                    :
                    <DragDropContext
                        onDragEnd={onDragEnd}
                    >
                        {Array.isArray(status) && status.map((s, index) => {
                            
                            const totals = totalsByStatus[s.id];
                            const sCurrencyFilter = selectedCurrencyFilter.find(cf => cf.statusId == s.id)?.selected || '';
                            const anchorEl = anchorEls[s.id] || null;
                            return (
                                <Droppable 
                                    droppableId={String(s.id)}
                                    type="COLUMN" 
                                    direction="vertical"
                                    key={`droppable-${s.id}`}
                                    renderClone={(provided, snapshot, rubric) => {
                                        const sourceColumn = filteredContactsByStatus[rubric.source.droppableId] || [];
                                        const contact = sourceColumn[rubric.source.index];
                                        return (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                style={{
                                                    ...provided.draggableProps.style,
                                                    opacity: snapshot.isDragging ? 0.8 : 1,
                                                }}
                                            >
                                                <ContactCard contact={contact} />
                                            </div>
                                        );
                                    }}
                                >
                                    {provided => (
                                        <Box 
                                            className={classes.column}
                                            style={{ marginRight: index < (status.length - 1) ? '10px' : '0' }}
                                        >
                                            <Box component="header" className={classes.columnHeader}>
                                                <Box component="label">
                                                    {s.name} ({getColumnCountLabel(s.id)})
                                                </Box>
                                                <Tooltip
                                                    arrow
                                                    className={classes.tooltip}
                                                    title={
                                                        <>
                                                            <Typography fontSize={20}>Valores Recorrentes: R$ {totals.recurringValue}</Typography>
                                                            <Typography fontSize={20}>Valores Únicos: R$ {totals.singleValue}</Typography>
                                                            <Typography fontSize={20}>Valores dos Contratos: R$ {totals.contractValue}</Typography>
                                                        </>
                                                    }
                                                >
                                                    <Box component="span" style={{ float: 'right' }}>
                                                        R$ {totals[sCurrencyFilter]}
                                                    </Box>
                                                </Tooltip>
                                                <IconButton
                                                    aria-label="more-icon"
                                                    onClick={(event) => handleClick(event, s.id)}
                                                    id="more-icon"
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>
                                                <Popover
                                                    id={`menu-filter-${s.id}`}
                                                    key={`menu-filter-${s.id}`}
                                                    anchorEl={anchorEl}
                                                    keepMounted
                                                    open={Boolean(anchorEl)}
                                                    onClose={() => handleClose(s.id)}
                                                    anchorOrigin={{
                                                        vertical: 'bottom',
                                                        horizontal: 'center',
                                                    }}
                                                    transformOrigin={{
                                                        vertical: 'top',
                                                        horizontal: 'right',
                                                    }}
                                                    classes={{ paper: classes.customPaper }}
                                                >
                                                    <List
                                                        component="nav"
                                                        aria-label={`menu-filter-${s.id}`}
                                                    >
                                                        <ListItem style={{ display: "block" }}>
                                                            <SingleSelect
                                                                key={`currencyFilter${s.id}`}
                                                                id={`currencyFilter${s.id}`}
                                                                title={<>Exibir informação de <b>Valor Total</b> de:</>}
                                                                displayText="Selecionar Filtro"
                                                                name="currencyFilter"
                                                                options={currencyFilter}
                                                                onSelected={(id) => {
                                                                    handleSelectedCurrencyFilter(id, s.id);
                                                                }}
                                                                selected={sCurrencyFilter}
                                                                closeOnSelect
                                                            />
                                                        </ListItem>
                                                    </List>
                                                </Popover>
                                            </Box>

                                            <Box
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                                className={classes.columnCards}
                                                onScroll={handleColumnScroll(s.id)}
                                            >
                                                {filteredContactsByStatus[s.id].map((contact, index) => (
                                                    <Draggable
                                                        key={`draggable-${contact.id}`}
                                                        draggableId={String(contact.id)}
                                                        index={index}
                                                    >
                                                        {(dragProvided, dragSnapshot) => (
                                                            <Box
                                                                ref={dragProvided.innerRef}
                                                                {...dragProvided.draggableProps}
                                                                {...dragProvided.dragHandleProps}
                                                                className={classes.draggableWrapper}
                                                                style={{
                                                                    ...dragProvided.draggableProps.style,
                                                                    marginBottom: 15,
                                                                    opacity: dragSnapshot.isDragging ? 0.9 : 1,
                                                                }}
                                                            >
                                                                <ContactCard contact={contact} />
                                                            </Box>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                                {columnMeta[s.id]?.loading && (
                                                    <CircularProgress
                                                        color="primary"
                                                        size={22}
                                                        style={{ margin: "8px auto", display: "block" }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                </Droppable>
                            );
                        })}
                    </DragDropContext>
            }
            <ContactModal
                open={contactModal != null ? true : false}
                onClose={handleContactInfoClose}
                contactId={contactModal}
                tags={tags.filter((tag) => tag.id >= 1 && tag.status === 1)}
            />
            <ScheduleMessageListModal
                ticketId={scheduleModal?.ticketId || null}
                contactId={scheduleModal?.contactId || null}
                connectionId={scheduleModal?.whatsappId || null}
                open={scheduleModal?.ticketId && scheduleModal?.contactId && scheduleModal?.whatsappId ? true : false}
                onClose={handleScheduleMessageClosed}
            />
            <RenderChatRelatory
                title={`#${messageHistoryModal}`}
                open={messageHistoryModal != null}
                onClose={e => { handleMessageHistoryClose() }}
                ticketId={messageHistoryModal}
            />
            <ContactTransferModal
                open={transferTicketModal?.contact && transferTicketModal?.ticket ? true : false}
                onClose={handleTransferClose}
                contact={transferTicketModal?.contact}
                ticket={transferTicketModal?.ticket}
                isDialog={true}
                user={user}
            />
            <Popover
                id={`menu-actions`}
                key={`menu-actions`}
                anchorReference="anchorPosition"
                anchorPosition={{top: anchorPosition.top, left: anchorPosition.left}}
                open={actionProps != null ? true : false}
                onClose={handleButtonsClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                classes={{ paper: classes.customPaper }}
            >
                <List 
                    component="nav" 
                    aria-label="card actions"
                >
                    <ListItem 
                        key="contactDetails"
                        button
                        onClick={(event) => { 
                            event.preventDefault();
                            handleButtonsClose();
                            handleContactInfoOpen(actionProps?.contact?.id); 
                        }}
                        disabled={!actionProps?.contact?.id ? true : false}
                    >
                        <ListItemIcon style={{ minWidth: '30px' }}><PermContactCalendarOutlinedIcon /></ListItemIcon>
                        <ListItemText primary="Informações de Contato" />
                    </ListItem>
                    <ListItem 
                        key="scheduleMessage"
                        button
                        onClick={(event) => { 
                            event.preventDefault();
                            handleButtonsClose();
                            handleScheduleMessageOpen(actionProps?.contact?.id, actionProps?.Ticket?.id, actionProps?.whatsappId);
                        }}
                        disabled={!actionProps?.contact?.id || !actionProps?.whatsappId ? true : false}
                    >
                        <ListItemIcon style={{ minWidth: '30px' }}><AccessAlarmIcon /></ListItemIcon>
                        <ListItemText primary="Agendar Mensagem" />
                    </ListItem>
                    <ListItem 
                        key="messageHistory"
                        button
                        onClick={(event) => { 
                            event.preventDefault();
                            handleButtonsClose();
                            handleMessageHistoryOpen(actionProps?.Ticket?.id);
                        }}
                        disabled={!actionProps?.Ticket?.id ? true : false}
                    >
                        <ListItemIcon style={{ minWidth: '30px' }}><MessageIcon /></ListItemIcon>
                        <ListItemText primary="Histórico de Mensagens" />
                    </ListItem>
                    <ListItem
                        key="ticketTransfer"
                        button
                        onClick={(event) => { 
                            event.preventDefault();
                            handleButtonsClose();
                            handleTransferOpen(actionProps?.contact, actionProps?.Ticket);
                        }}
                        disabled={!actionProps?.contact || !actionProps.Ticket || actionProps?.isTicketClosed == true ? true : false}
                    >
                        <ListItemIcon style={{ minWidth: '30px' }}><SyncAltOutlinedIcon /></ListItemIcon>
                        <ListItemText primary="Transferir Atendimento" />
                    </ListItem>
                </List>
            </Popover>
        </Box>
    );
}